import { Icon } from '../../atoms/Icon/view';
import { Text } from '../../atoms/Text/view';
import type { FeatureEntitlement } from '~/store/entitlements/interfaces';

interface PropsUpgradePrompt {
  entitlement?: FeatureEntitlement;
  featureSlug?: string;
  title?: string;
  description?: string;
  ctaText?: string;
  onClickCta?: () => void;
}

const PLAN_LABEL: Record<string, string> = {
  free: 'Free',
  freemium: 'Freemium',
  premium_basic: 'Premium Basic',
  premium_pro: 'Premium Pro',
  premium_enterprise: 'Premium Enterprise',
};

function copyFor(entitlement: FeatureEntitlement | undefined): {
  pill: string;
  title: string;
  description: string;
} {
  if (!entitlement) {
    return {
      pill: 'Premium',
      title: 'Recurso premium',
      description: 'Atualize seu plano para acessar esta funcionalidade.',
    };
  }
  if (entitlement.state === 'expired') {
    return {
      pill: 'Expirado',
      title: 'Seu acesso expirou',
      description:
        'Renove o plano para continuar usando esta funcionalidade na sua conta.',
    };
  }
  if (entitlement.state === 'freemium' && (entitlement.quotaRemaining ?? 0) <= 0) {
    return {
      pill: entitlement.upgradeRequired
        ? PLAN_LABEL[entitlement.upgradeRequired] ?? entitlement.upgradeRequired
        : 'Premium',
      title: 'Limite mensal atingido',
      description:
        'Você usou o limite gratuito deste mês. Faça upgrade para acesso ilimitado.',
    };
  }
  if (entitlement.upgradeRequired) {
    const label =
      PLAN_LABEL[entitlement.upgradeRequired] ?? entitlement.upgradeRequired;
    return {
      pill: label,
      title: `Disponível no ${label}`,
      description: 'Atualize seu plano e libere este recurso na sua conta.',
    };
  }
  return {
    pill: 'Premium',
    title: 'Recurso bloqueado',
    description:
      'Este recurso não está disponível na sua conta. Fale com nosso time se acredita que deveria ter acesso.',
  };
}

export default function UpgradePrompt({
  entitlement,
  featureSlug: _featureSlug,
  title,
  description,
  ctaText = 'Conhecer planos',
  onClickCta,
}: PropsUpgradePrompt) {
  const copy = copyFor(entitlement);
  const finalTitle = title ?? copy.title;
  const finalDescription = description ?? copy.description;

  return (
    <article className="relative overflow-hidden rounded-medium border border-support-01 bg-intense-01 shadow-level1">
      <div className="flex h-[6px]">
        <div className="w-1/2 bg-primary-01" />
        <div className="w-1/4 bg-secondary-01" />
        <div className="w-1/4 bg-support-01" />
      </div>

      <div className="p-medium">
        <div className="flex items-center gap-quark">
          <span className="inline-flex items-center rounded-full bg-primary-01 px-nano py-quark text-[10px] font-bold uppercase tracking-[0.12em] color-intense-01">
            {copy.pill}
          </span>
          <span className="inline-flex items-center gap-quark text-caption color-intense-05">
            <Icon name="icon-graphic-up" size={12} color="#3F7D5F" rounded={false} />
            <span>plano necessário</span>
          </span>
        </div>

        <Text className="mt-smaller text-headingH2 color-complement-01">
          {finalTitle}
        </Text>
        <Text className="mt-quark text-bodySM color-intense-06">
          {finalDescription}
        </Text>

        <button
          type="button"
          onClick={onClickCta}
          className="mt-medium inline-flex items-center gap-quark rounded-nano border border-primary-01 px-small py-quark text-bodyMD font-medium color-primary-01 transition-colors hover:bg-support-01 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-01/30"
        >
          {ctaText}
          <Icon name="icon-chevron-right" size={14} color="#1D4733" rounded={false} />
        </button>
      </div>
    </article>
  );
}
